// ============================================
// App.js - Updated with Settings route
// ============================================
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

// Hooks
import { useAuth } from './hooks/useAuth';
import { applyActionCode } from 'firebase/auth';
import { auth } from './firebase';

// Components
import Homepage from './components/Homepage';
import Settings from './components/Settings';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import LoadingSpinner from './components/common/LoadingSpinner';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';

// Utils
import { ROUTES } from './utils/constants';
import theme from './styles/theme';

// Import CSS files
import './styles/global.css';
import './styles/animations.css';
import './styles/components/porchlight.css';
import './styles/components/modal.css';

// Handles Firebase email action links (e.g., verifyEmail)
function EmailActionHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshAuthUser } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');
    if (mode === 'verifyEmail' && oobCode) {
      (async () => {
        try {
          await applyActionCode(auth, oobCode);
        } catch (_) {
          // ignore invalid/expired code errors in UI
        } finally {
          // Always refresh and clean the URL
          try {
            await refreshAuthUser?.();
          } catch (_) {}
          navigate(location.pathname, { replace: true });
        }
      })();
    }
  }, [location.search, navigate, refreshAuthUser]);

  return null;
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner fullScreen />;
  }
  
  return user ? children : <Navigate to={ROUTES.LOGIN} replace />;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner fullScreen />;
  }
  return <Navigate to={user ? ROUTES.HOME : ROUTES.LOGIN} replace />;
};

// Public Route Component: prevents authenticated users from viewing auth pages,
// but allows a short grace period (via sessionStorage flag) to show success
// messages after actions like signup.
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <LoadingSpinner fullScreen />;
  }
  if (user) {
    const isBrowser = typeof window !== 'undefined';
    const allowGrace = isBrowser && window.sessionStorage.getItem('authPublicRouteGrace') === '1';
    const isAuthPage = [ROUTES.SIGNUP, ROUTES.LOGIN, ROUTES.FORGOT_PASSWORD].includes(location.pathname);
    if (allowGrace && isAuthPage) {
      return children;
    }
    return <Navigate to={ROUTES.HOME} replace />;
  }
  return children;
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <EmailActionHandler />
        <Routes>
          <Route path={ROUTES.SIGNUP} element={<PublicRoute><Signup /></PublicRoute>} />
          <Route path={ROUTES.LOGIN} element={<PublicRoute><Login /></PublicRoute>} />
          <Route 
            path={ROUTES.HOME} 
            element={
              <ProtectedRoute>
                <Homepage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path={ROUTES.SETTINGS} 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={ROUTES.PRIVACY} element={<PrivacyPolicy />} />
          <Route path={ROUTES.TERMS} element={<TermsOfService />} />
          <Route path="/" element={<RootRedirect />} />
          {/* Catch-all: redirect unknown routes to root redirect */}
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;